import { FormTypes } from '@oneblink/types'
import { v4 as uuidv4 } from 'uuid'

import {
  findElement,
  replaceElementOptions,
} from '../../definition-manipulation'

import makeIsShowing from '../../is-element-showing'

export interface PeopleLike {
  Title?: string
  First_Name: string
  Last_Name: string
  Phone: string
  Mobile?: string
  Email: string
  Other_email?: string
  Job_Function?: string
}

export const generateName = (p: Partial<PeopleLike>) =>
  [p.First_Name, p.Last_Name].filter(Boolean).join(' ')

export function findPerson(
  targetPerson: string,
  people: Partial<PeopleLike>[],
) {
  return people.find((person) => generateName(person) === targetPerson)
}

export function setToSortedArray(set: Set<string>) {
  return Array.from(set).sort((a, b) => {
    const aLower = a.toLowerCase()
    const bLower = b.toLowerCase()

    if (aLower < bLower) {
      return -1
    }
    if (bLower < aLower) {
      return 1
    }

    return 0
  })
}

export function getPeople(
  submission: Record<string, unknown>,
  elements: FormTypes.FormElement[],
): PeopleLike[] {
  const isElementVisible = makeIsShowing(submission, elements)
  const [
    soleTraderEl,
    partnershipEl,
    companyEl,
    associationEl,
    govAgencyEl,
    otherEl,
  ] = [
    'Sole_trader',
    'Partnership',
    'Company',
    'Association',
    'Government_Agency',
    'Other',
  ]

  const people = [
    ...(soleTraderEl && isElementVisible(soleTraderEl)
      ? (submission.Sole_trader as PeopleLike[])
      : []),
    ...(partnershipEl && isElementVisible(partnershipEl)
      ? (submission.Partnership as PeopleLike[])
      : []),
    ...(companyEl && isElementVisible(companyEl)
      ? (submission.Company as PeopleLike[])
      : []),
    ...(associationEl && isElementVisible(associationEl)
      ? (submission.Association as PeopleLike[])
      : []),
    ...(govAgencyEl && isElementVisible(govAgencyEl)
      ? (submission.Government_Agency as PeopleLike[])
      : []),
    ...(otherEl && isElementVisible(otherEl)
      ? (submission.Other as PeopleLike[])
      : []),
  ].filter(Boolean)

  return people
}

export function getContactNames(
  submission: Record<string, unknown>,
  elements: FormTypes.FormElement[],
): Set<string> {
  return getPeople(submission, elements).reduce((memo: Set<string>, person) => {
    if (person.First_Name && person.First_Name !== '') {
      memo.add(generateName(person))
    }

    return memo
  }, new Set<string>())
}

export function getContactEmails(
  submission: Record<string, unknown>,
  elements: FormTypes.FormElement[],
): Set<string> {
  const emails = getPeople(submission, elements).reduce(
    (memo: Set<string>, person) => {
      if (person.Email && person.Email !== 'Other') {
        memo.add(person.Email)
      }
      if (person.Email === 'Other' && person.Other_email) {
        memo.add(person.Other_email)
      }

      return memo
    },
    new Set<string>(),
  )
  if (submission._Email) {
    emails.add(submission._Email as string)
  }
  return emails
}

export function getContactPhones(
  submission: Record<string, unknown>,
  elements: FormTypes.FormElement[],
): Set<string> {
  return getPeople(submission, elements).reduce((memo: Set<string>, person) => {
    if (person.Phone) memo.add(person.Phone)
    if (person.Mobile) memo.add(person.Mobile)
    return memo
  }, new Set<string>())
}

export function getOtherPerson(
  elementNamesMap: Partial<PeopleLike>,
  submission: Record<string, unknown>,
  elements: FormTypes.FormElement[],
): Partial<PeopleLike> | undefined {
  const contactElements = Object.values(elementNamesMap)

  //need to do this as submission is wiped during draft restore
  const _submission = submission || {}

  const isElementVisible = makeIsShowing(_submission, elements)

  if (contactElements.some(isElementVisible)) {
    return {
      First_Name: elementNamesMap.First_Name
        ? (submission[elementNamesMap.First_Name] as string)
        : '',
      Last_Name: elementNamesMap.Last_Name
        ? (submission[elementNamesMap.Last_Name] as string)
        : '',
      Phone: elementNamesMap.Phone
        ? (submission[elementNamesMap.Phone] as string)
        : '',
      Email: elementNamesMap.Email
        ? (submission[elementNamesMap.Email] as string)
        : '',
      Job_Function: elementNamesMap.Job_Function
        ? (submission[elementNamesMap.Job_Function] as string)
        : '',
    }
  }
}

export function populateBusinessContactsEmail(
  submission: Record<string, unknown>,
  definition: FormTypes.Form,
  email?: string,
) {
  const businessStructures = [
    'Sole_trader',
    'Partnership',
    'Company',
    'Association',
    'Government_Agency',
    'Other',
  ]

  businessStructures.forEach((businessStructure) => {
    const businessContactEl = findElement(definition, businessStructure)

    if (businessContactEl) {
      const businessEmail = email ? email : (submission._Email as string)
      const options = businessEmail
        ? [
            {
              id: uuidv4(),
              label: businessEmail,
              value: businessEmail,
            },
          ]
        : []

      replaceElementOptions(
        businessContactEl.value as unknown as FormTypes.RepeatableSetElement,
        'Email',
        options,
      )
    }
  })

  return definition
}
